"use client";

import {
  Box,
  Flex,
  HStack,
  Icon,
  SimpleGrid,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useTranslations } from "next-intl";
import { useCallback, useEffect, useState } from "react";
import {
  BsArrowClockwise,
  // BsClock,
  BsCreditCard,
  BsShield,
} from "react-icons/bs";
import { IconType } from "react-icons";
import { PRICING_IDS } from "../_constants/pricing.ids";

const SLIDE_KEYS = [
  "sslPayment",
  "cancelAnytime",
  "noCreditCard",
  // "moneyBack",
] as const;

const SLIDE_ICONS: Record<(typeof SLIDE_KEYS)[number], IconType> = {
  sslPayment: BsShield,
  cancelAnytime: BsArrowClockwise,
  noCreditCard: BsCreditCard,
  // moneyBack: BsClock,
};

const ROTATE_MS = 4500;

export default function TrustSlideshow() {
  const t = useTranslations("pricing.trustSignals");
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  const goTo = useCallback((index: number) => {
    setActiveIndex(index % SLIDE_KEYS.length);
  }, []);

  useEffect(() => {
    if (isPaused) return;
    const timer = window.setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % SLIDE_KEYS.length);
    }, ROTATE_MS);
    return () => window.clearInterval(timer);
  }, [isPaused]);

  return (
    <Box
      id={PRICING_IDS.sections.trust}
      w="full"
      scrollMarginTop="5rem"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      onFocusCapture={() => setIsPaused(true)}
      onBlurCapture={() => setIsPaused(false)}
    >
      <Box display={{ base: "block", lg: "none" }}>
        <VStack gap={4} w="full">
          <Box
            w="full"
            rounded="3xl"
            borderWidth="0.5px"
            borderColor="primary.200"
            bg="white"
            boxShadow="md"
            px={6}
            py={5}
            minH="108px"
          >
            <TrustSlideContent
              key={SLIDE_KEYS[activeIndex]}
              icon={SLIDE_ICONS[SLIDE_KEYS[activeIndex]]}
              title={t(SLIDE_KEYS[activeIndex])}
              description={t(`${SLIDE_KEYS[activeIndex]}Description`)}
            />
          </Box>
          <HStack gap={2} justify="center" pt={2}>
            {SLIDE_KEYS.map((key, index) => (
              <Box
                key={key}
                as="button"
                aria-label={t(key)}
                aria-current={index === activeIndex}
                w={index === activeIndex ? "24px" : "8px"}
                h="8px"
                rounded="full"
                bg={index === activeIndex ? "primary.500" : "neutral.300"}
                transition="all 0.25s ease"
                onClick={() => goTo(index)}
              />
            ))}
          </HStack>
        </VStack>
      </Box>

      <SimpleGrid
        display={{ base: "none", lg: "grid" }}
        columns={3}
        gap={4}
        w="full"
      >
        {SLIDE_KEYS.map((key) => (
          <Box
            key={key}
            rounded="2xl"
            borderWidth="0.5px"
            borderColor="neutral.200"
            bg="white"
            p={5}
            boxShadow="sm"
            transition="all 0.2s ease"
            _hover={{
              borderColor: "primary.200",
              boxShadow: "md",
              transform: "translateY(-2px)",
            }}
          >
            <TrustSlideContent
              icon={SLIDE_ICONS[key]}
              title={t(key)}
              description={t(`${key}Description`)}
            />
          </Box>
        ))}
      </SimpleGrid>
    </Box>
  );
}

function TrustSlideContent({
  icon,
  title,
  description,
}: {
  icon: IconType;
  title: string;
  description: string;
}) {
  return (
    <Flex gap={4} align="flex-start">
      <Flex
        boxSize={10}
        rounded="xl"
        bg="primary.50"
        align="center"
        justify="center"
        flexShrink={0}
      >
        <Icon as={icon} boxSize={5} color="primary.600" />
      </Flex>
      <Box>
        <Text textStyle="sm" fontWeight="semibold" color="neutral.800" mb={1}>
          {title}
        </Text>
        <Text textStyle="xs" color="neutral.500" lineHeight="tall">
          {description}
        </Text>
      </Box>
    </Flex>
  );
}
